const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Función para obtener el token del localStorage
const getToken = () => {
  return localStorage.getItem('token');
};

// Función para hacer peticiones autenticadas
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    const text = await response.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        if (!response.ok) {
          throw new Error(text.slice(0, 200) || 'Error en la petición');
        }
      }
    }

    if (!response.ok) {
      const err = new Error(data.message || 'Error en la petición');
      err.status = response.status;
      err.code = data.code || null;
      err.data = data;
      throw err;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

/** Reintenta la acción con confirmar_saldo_negativo si el backend responde 409 SALDO_QUEDARIA_NEGATIVO. */
export async function withSaldoNegativoConfirm(run) {
  try {
    return await run(false);
  } catch (err) {
    if (err?.status === 409 && err?.code === 'SALDO_QUEDARIA_NEGATIVO') {
      const saldoRes = err.data?.saldo_resultante;
      const msg =
        err.message ||
        (saldoRes != null
          ? `El saldo quedaría en $${Number(saldoRes).toLocaleString('es-AR', { minimumFractionDigits: 2 })}. ¿Continuar?`
          : 'El saldo quedaría negativo. ¿Continuar de todos modos?');
      if (typeof window !== 'undefined' && window.confirm(msg)) {
        return await run(true);
      }
      const cancel = new Error('Operación cancelada');
      cancel.cancelled = true;
      throw cancel;
    }
    throw err;
  }
}

// Auth - login con errores detallados para depuración (Hostinger, etc.)
export const authAPI = {
  login: async (username, password) => {
    const url = `${API_URL}/auth/login`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const contentType = response.headers.get('content-type') || '';
      const text = await response.text();

      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (parseErr) {
        const isHtml = text.trim().startsWith('<') || contentType.includes('text/html');
        return {
          success: false,
          message: isHtml
            ? 'El servidor respondió con una página en lugar de la API.'
            : 'La respuesta del servidor no es JSON válido.',
          detail: `HTTP ${response.status}. ${text.length > 200 ? text.substring(0, 200) + '…' : text}`,
          status: response.status,
        };
      }

      if (!response.ok) {
        const msg = data.message || data.error || 'Error en el servidor';
        return {
          success: false,
          message: msg,
          detail: `HTTP ${response.status}. ${response.status === 404 ? 'La ruta /api/auth/login no existe (revise que el backend reciba las peticiones).' : ''}`,
          status: response.status,
        };
      }

      if (data.success && data.token && data.user) {
        return { success: true, token: data.token, user: data.user };
      }
      return {
        success: false,
        message: data.message || 'Respuesta inesperada del servidor.',
        detail: JSON.stringify(data).substring(0, 300),
        status: response.status,
      };
    } catch (err) {
      const isNetwork = err.name === 'TypeError' && (err.message === 'Failed to fetch' || err.message.includes('NetworkError'));
      return {
        success: false,
        message: isNetwork
          ? 'No se pudo conectar con el servidor. Revise la URL de la API y que el servidor esté activo.'
          : (err.message || 'Error de conexión'),
        detail: err.message || String(err),
        status: null,
      };
    }
  },
  verify: async () => {
    return apiRequest('/auth/verify');
  },
};

// Usuarios
export const usuariosAPI = {
  getActivos: async () => apiRequest('/usuarios/activos'),
  getAll: async () => apiRequest('/usuarios'),
  getById: async (id) => apiRequest(`/usuarios/${id}`),
  create: async (data) => apiRequest('/usuarios', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/usuarios/${id}`, { method: 'DELETE' }),
  getPermisos: async () => apiRequest('/usuarios/permisos/list'),
  getPreferencias: async (clave) => apiRequest(`/usuarios/me/preferencias${clave ? `?clave=${encodeURIComponent(clave)}` : ''}`),
  savePreferencias: async (clave, valor) => apiRequest('/usuarios/me/preferencias', { method: 'PUT', body: JSON.stringify({ clave, valor }) }),
  getFormaPagoFondos: async () => apiRequest('/usuarios/me/forma-pago-fondos'),
  saveFormaPagoFondos: async (items) => apiRequest('/usuarios/me/forma-pago-fondos', { method: 'PUT', body: JSON.stringify(Array.isArray(items) ? items : { data: items }) }),
  getFormaPagoFondosForUser: async (usuarioId) =>
    apiRequest(`/usuarios/forma-pago-fondos?usuario_id=${encodeURIComponent(usuarioId)}`),
  saveFormaPagoFondosForUser: async (usuarioId, items) =>
    apiRequest('/usuarios/forma-pago-fondos', {
      method: 'PUT',
      body: JSON.stringify({ usuario_id: usuarioId, items: Array.isArray(items) ? items : [] }),
    }),
};

// Tipos de Clientes
export const tiposClientesAPI = {
  getAll: async () => apiRequest('/tipos-clientes'),
  getAllIncludingInactive: async () => apiRequest('/tipos-clientes/all'),
  getById: async (id) => apiRequest(`/tipos-clientes/${id}`),
  create: async (data) => apiRequest('/tipos-clientes', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/tipos-clientes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/tipos-clientes/${id}`, { method: 'DELETE' }),
};

// Grupos (de clientes: forma de envío de resúmenes)
export const gruposAPI = {
  getAll: async () => apiRequest('/grupos'),
  getAllIncludingInactive: async () => apiRequest('/grupos/all'),
  getById: async (id) => apiRequest(`/grupos/${id}`),
  create: async (data) => apiRequest('/grupos', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/grupos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/grupos/${id}`, { method: 'DELETE' }),
  getClientes: async (id) => apiRequest(`/grupos/${id}/clientes`),
  updateDestinatarios: async (id, destinatarios) =>
    apiRequest(`/grupos/${id}/destinatarios`, {
      method: 'PUT',
      body: JSON.stringify({ destinatarios }),
    }),
  getClientesDisponibles: async (id) => apiRequest(`/grupos/${id}/clientes-disponibles`),
  assignClientes: async (id, cliente_ids) =>
    apiRequest(`/grupos/${id}/clientes`, {
      method: 'PUT',
      body: JSON.stringify({ cliente_ids }),
    }),
  armarMensaje: async (id, body) =>
    apiRequest(`/grupos/${id}/armar-mensaje`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// Clientes
export const clientesAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/clientes?${queryString}`);
  },
  getById: async (id) => apiRequest(`/clientes/${id}`),
  create: async (data) => apiRequest('/clientes', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/clientes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/clientes/${id}`, { method: 'DELETE' }),
  saldosAl: async (cliente_ids, saldo_al) =>
    apiRequest('/clientes/saldos-al', {
      method: 'POST',
      body: JSON.stringify({ cliente_ids, saldo_al }),
    }),
};

// Tipos de Proveedores
export const tiposProveedoresAPI = {
  getAll: async () => apiRequest('/tipos-proveedores'),
  getAllIncludingInactive: async () => apiRequest('/tipos-proveedores/all'),
  getById: async (id) => apiRequest(`/tipos-proveedores/${id}`),
  create: async (data) => apiRequest('/tipos-proveedores', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/tipos-proveedores/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/tipos-proveedores/${id}`, { method: 'DELETE' }),
};

// Proveedores
export const proveedoresAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/proveedores?${queryString}`);
  },
  getById: async (id) => apiRequest(`/proveedores/${id}`),
  create: async (data) => apiRequest('/proveedores', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/proveedores/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/proveedores/${id}`, { method: 'DELETE' }),
};

// Empleados
export const empleadosAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/empleados?${queryString}`);
  },
  getById: async (id) => apiRequest(`/empleados/${id}`),
  create: async (data) => apiRequest('/empleados', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/empleados/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/empleados/${id}`, { method: 'DELETE' }),
};

// Tipos de movimiento de persona (catálogo a definir a futuro)
export const tiposMovimientoPersonaAPI = {
  getAll: async () => apiRequest('/tipos-movimiento-persona'),
  getAllIncludingInactive: async () => apiRequest('/tipos-movimiento-persona/all'),
  getById: async (id) => apiRequest(`/tipos-movimiento-persona/${id}`),
  create: async (data) => apiRequest('/tipos-movimiento-persona', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/tipos-movimiento-persona/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/tipos-movimiento-persona/${id}`, { method: 'DELETE' }),
};

// Formas de Pago
export const formasPagoAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/formas-pago?${queryString}`);
  },
  getDisponibles: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/formas-pago/disponibles?${queryString}`);
  },
  getById: async (id) => apiRequest(`/formas-pago/${id}`),
  create: async (data) => apiRequest('/formas-pago', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/formas-pago/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/formas-pago/${id}`, { method: 'DELETE' }),
};

export const cotizacionesAPI = {
  getDolarBna: async (fecha) => apiRequest(`/cotizaciones/dolar-bna?fecha=${encodeURIComponent(fecha)}`),
};

// Cajas
export const cajasAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/cajas?${queryString}`);
  },
  getById: async (id) => apiRequest(`/cajas/${id}`),
  create: async (data) => apiRequest('/cajas', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/cajas/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/cajas/${id}`, { method: 'DELETE' }),
  ajusteSaldo: async (id, data) =>
    apiRequest(`/cajas/${id}/ajuste-saldo`, { method: 'POST', body: JSON.stringify(data) }),
};

// Movimientos Predefinidos
export const movimientosPredefinidosAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/movimientos-predefinidos?${queryString}`);
  },
  getById: async (id) => apiRequest(`/movimientos-predefinidos/${id}`),
  create: async (data) => apiRequest('/movimientos-predefinidos', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/movimientos-predefinidos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/movimientos-predefinidos/${id}`, { method: 'DELETE' }),
};

// Movimientos
export const movimientosAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/movimientos?${queryString}`);
  },
  createDebito: async (data) => apiRequest('/movimientos/debito', { method: 'POST', body: JSON.stringify(data) }),
  createDebitosMasivos: async (data) => apiRequest('/movimientos/debitos-masivos', { method: 'POST', body: JSON.stringify(data) }),
  createAbonoMensual: async (data) => apiRequest('/movimientos/abono-mensual', { method: 'POST', body: JSON.stringify(data) }),
  createCredito: async (data) => apiRequest('/movimientos/credito', { method: 'POST', body: JSON.stringify(data) }),
  getByCliente: async (clienteId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/movimientos/cliente/${clienteId}?${queryString}`);
  },
  getByProveedor: async (proveedorId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/movimientos/proveedor/${proveedorId}?${queryString}`);
  },
  getHuerfanosOp: async () => apiRequest('/movimientos/huerfanos-op'),
  getHuerfanosPagoCuenta: async () => apiRequest('/movimientos/huerfanos-pago-cuenta'),
  anularHuerfano: async (id, tipo = 'proveedor') => {
    const q = tipo === 'cliente' ? '?tipo=cliente' : '';
    return apiRequest(`/movimientos/${id}/anular-huerfano${q}`, { method: 'POST' });
  },
  delete: async (id) => apiRequest(`/movimientos/${id}`, { method: 'DELETE' }),
};

// Recibos de Cobro
export const recibosCobroAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/recibos-cobro?${queryString}`);
  },
  getById: async (id) => apiRequest(`/recibos-cobro/${id}`),
  getByMovimientoId: async (movimientoId) => apiRequest(`/recibos-cobro/por-movimiento/${movimientoId}`),
  crear: async (data) => apiRequest('/recibos-cobro/crear', { method: 'POST', body: JSON.stringify(data) }),
  crearCompleto: async (data) => apiRequest('/recibos-cobro/crear-completo', { method: 'POST', body: JSON.stringify(data) }),
  getCarteraCheques: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/recibos-cobro/cartera-cheques${q ? `?${q}` : ''}`);
  },
  marcarRechazadoCartera: async (id, data) =>
    apiRequest(`/recibos-cobro/cartera-cheques/${id}/marcar-rechazado`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),
  reintegrarChequeCartera: async (id, data) =>
    apiRequest(`/recibos-cobro/cartera-cheques/${id}/reintegrar`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),
  getGastosRechazoCheque: async (id) =>
    apiRequest(`/recibos-cobro/cartera-cheques/${id}/gastos-rechazo`),
  getChequesEmitidos: async () => apiRequest('/recibos-cobro/cheques-emitidos'),
  getAlertasCheques: async () => apiRequest('/recibos-cobro/alertas-cheques'),
  anular: async (id) => apiRequest(`/recibos-cobro/${id}/anular`, { method: 'POST' }),
};

// Recibos de Cobro por grupo de clientes
export const recibosCobroGrupoAPI = {
  getAll: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/recibos-cobro-grupo${q ? `?${q}` : ''}`);
  },
  getById: async (id) => apiRequest(`/recibos-cobro-grupo/${id}`),
  crear: async (data) => apiRequest('/recibos-cobro-grupo/crear', { method: 'POST', body: JSON.stringify(data) }),
  anular: async (id) => apiRequest(`/recibos-cobro-grupo/${id}/anular`, { method: 'POST' }),
};

export const pagosAPI = {
  crear: async (data) => apiRequest('/pagos/crear', { method: 'POST', body: JSON.stringify(data) }),
  getById: async (id) => apiRequest(`/pagos/${id}`),
  getByMovimientoId: async (movimientoId) => apiRequest(`/pagos/por-movimiento/${movimientoId}`),
  anular: async (id) => apiRequest(`/pagos/${id}/anular`, { method: 'POST' }),
};

// Fondos (depósito/extracción de cajas y cheques de terceros a cuenta bancaria)
export const fondosAPI = {
  getAll: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/fondos${q ? `?${q}` : ''}`);
  },
  getEntidades: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/fondos/entidades${q ? `?${q}` : ''}`);
  },
  getMovimientosCaja: async (id, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/fondos/movimientos/caja/${id}${q ? `?${q}` : ''}`);
  },
  getMovimientosCuenta: async (id, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/fondos/movimientos/cuenta/${id}${q ? `?${q}` : ''}`);
  },
  getById: async (id) => apiRequest(`/fondos/${id}`),
  crear: async (data) => apiRequest('/fondos', { method: 'POST', body: JSON.stringify(data) }),
};

export const cierresCajaAPI = {
  preparar: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/cierres-caja/preparar?${q}`);
  },
  crear: async (data) => apiRequest('/cierres-caja', { method: 'POST', body: JSON.stringify(data) }),
  listar: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/cierres-caja${q ? `?${q}` : ''}`);
  },
  getById: async (id) => apiRequest(`/cierres-caja/${id}`),
  anular: async (id) => apiRequest(`/cierres-caja/${id}/anular`, { method: 'POST' }),
  chequesEnCaja: async (cajaId) => apiRequest(`/cierres-caja/cheques-caja?caja_id=${cajaId}`),
};

// Bancos
export const bancosAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/bancos?${queryString}`);
  },
  getById: async (id) => apiRequest(`/bancos/${id}`),
  create: async (data) => apiRequest('/bancos', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/bancos/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/bancos/${id}`, { method: 'DELETE' }),
};

// Cuentas Bancarias
export const cuentasBancariasAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/cuentas-bancarias?${queryString}`);
  },
  getById: async (id) => apiRequest(`/cuentas-bancarias/${id}`),
  create: async (data) => apiRequest('/cuentas-bancarias', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/cuentas-bancarias/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/cuentas-bancarias/${id}`, { method: 'DELETE' }),
};

// Provincias
export const provinciasAPI = {
  getAll: async () => apiRequest('/provincias'),
  getById: async (id) => apiRequest(`/provincias/${id}`),
};

// Localidades
export const localidadesAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/localidades?${queryString}`);
  },
  getByProvincia: async (provinciaId) => apiRequest(`/localidades/provincia/${provinciaId}`),
  getById: async (id) => apiRequest(`/localidades/${id}`),
  create: async (data) => apiRequest('/localidades', { method: 'POST', body: JSON.stringify(data) }),
};

// Plantillas WhatsApp
export const whatsappTemplatesAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const suffix = qs ? `?${qs}` : '';
    const res = await apiRequest(`/whatsapp-templates${suffix}`);
    return res.success ? res : { success: false, data: [] };
  },
  create: async (data) => apiRequest('/whatsapp-templates', { method: 'POST', body: JSON.stringify(data) }),
  update: async (id, data) => apiRequest(`/whatsapp-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: async (id) => apiRequest(`/whatsapp-templates/${id}`, { method: 'DELETE' }),
  uploadImagen: async (id, file) => {
    const token = getToken();
    const form = new FormData();
    form.append('imagen', file);
    const response = await fetch(`${API_URL}/whatsapp-templates/${id}/imagen`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'No se pudo subir la imagen');
    return data;
  },
  deleteImagen: async (id) => apiRequest(`/whatsapp-templates/${id}/imagen`, { method: 'DELETE' }),
  /** URL de objeto para vista previa; quien la usa debe liberarla con URL.revokeObjectURL. */
  getImagenBlobUrl: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_URL}/whatsapp-templates/${id}/imagen`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return null;
    return URL.createObjectURL(await response.blob());
  },
};

// Historial de envíos WhatsApp
export const whatsappEnviosAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/whatsapp-envios?${qs}`);
  },
  create: async (data) => apiRequest('/whatsapp-envios', { method: 'POST', body: JSON.stringify(data) }),
};

const whatsappBaileysFetch = async (path, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}/whatsapp-baileys${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Error en servicio WhatsApp');
  }
  return data;
};

/** Baileys embebido o microservicio (misma API que proyecto Cursor). */
export const whatsappBaileysAPI = {
  getStatus: () => whatsappBaileysFetch('/status'),
  sendBatch: (items) =>
    whatsappBaileysFetch('/send-batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  getSendRateProfiles: () => whatsappBaileysFetch('/send-rate/profiles'),
  getSendRateSession: (profile) =>
    whatsappBaileysFetch(`/send-rate/session?profile=${encodeURIComponent(profile || 'normal')}`),
  getSendRateEstimate: (count, profile) =>
    whatsappBaileysFetch(
      `/send-rate/estimate?count=${encodeURIComponent(count)}&profile=${encodeURIComponent(profile || 'normal')}`
    ),
  sendSequential: (body) =>
    whatsappBaileysFetch('/send-sequential', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

export const comunicacionesAPI = {
  sendEmail: async (data) =>
    apiRequest('/comunicaciones/email', { method: 'POST', body: JSON.stringify(data) }),
};

// Auditoría
export const auditoriaAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/auditoria?${qs}`);
  },
  getModulos: async () => apiRequest('/auditoria/modulos'),
  getMensajesWhatsapp: async (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/auditoria/whatsapp?${qs}`);
  },
  getVersiones: async () => apiRequest('/auditoria/versiones'),
  getCambiosVersiones: async (desde, hasta) => {
    const params = new URLSearchParams();
    if (desde) params.set('desde', desde);
    if (hasta) params.set('hasta', hasta);
    const qs = params.toString();
    return apiRequest(`/auditoria/versiones/cambios${qs ? `?${qs}` : ''}`);
  },
  getUltimaActualizacionVersion: async () => apiRequest('/auditoria/versiones/ultima-actualizacion'),
};

// Backup
export const backupAPI = {
  listar: async () => apiRequest('/backup/listar'),
  estado: async () => apiRequest('/backup/estado'),
  ejecutar: async () => apiRequest('/backup/ejecutar', { method: 'POST' }),
  restaurar: async (nombre) => apiRequest(`/backup/restaurar/${encodeURIComponent(nombre)}`, { method: 'POST' }),
  eliminar: async (nombre) => apiRequest(`/backup/${encodeURIComponent(nombre)}`, { method: 'DELETE' }),
  getConfigDirectorio: async () => apiRequest('/backup/configuracion-directorio'),
  validarDirectorio: async (ruta) =>
    apiRequest('/backup/validar-directorio', { method: 'POST', body: JSON.stringify({ ruta }) }),
  saveConfigDirectorio: async (ruta) =>
    apiRequest('/backup/configuracion-directorio', { method: 'PUT', body: JSON.stringify({ ruta }) }),
};

export const gastosAPI = {
  getAll: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/gastos${q ? `?${q}` : ''}`);
  },
  crear: async (data) => apiRequest('/gastos/crear', { method: 'POST', body: JSON.stringify(data) }),
  anular: async (id) => apiRequest(`/gastos/${id}/anular`, { method: 'POST' }),
};

export const movimientosEmpleadosAPI = {
  getByEmpleado: async (empleadoId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/movimientos-empleados/empleado/${empleadoId}${q ? `?${q}` : ''}`);
  },
  debito: async (data) =>
    apiRequest('/movimientos-empleados/debito', { method: 'POST', body: JSON.stringify(data) }),
  devengacionSueldo: async (data) =>
    apiRequest('/movimientos-empleados/devengacion-sueldo', { method: 'POST', body: JSON.stringify(data) }),
};

export const pagosEmpleadosAPI = {
  crear: async (data) => apiRequest('/pagos-empleados/crear', { method: 'POST', body: JSON.stringify(data) }),
  anular: async (id) => apiRequest(`/pagos-empleados/${id}/anular`, { method: 'POST' }),
};

export const movimientosProgramadosAPI = {
  getAll: async () => apiRequest('/movimientos-programados'),
  create: async (data) =>
    apiRequest('/movimientos-programados', { method: 'POST', body: JSON.stringify(data) }),
  preview: async (id) =>
    apiRequest(`/movimientos-programados/${id}/preview`, { method: 'POST' }),
  aplicar: async (id) =>
    apiRequest(`/movimientos-programados/${id}/aplicar`, { method: 'POST' }),
  delete: async (id) => apiRequest(`/movimientos-programados/${id}`, { method: 'DELETE' }),
};

// Informes
export const informesAPI = {
  resumenGeneral: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/resumen-general?${queryString}`);
  },
  resumenCliente: async (clienteId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/resumen-cliente/${clienteId}?${queryString}`);
  },
  resumenTipo: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/resumen-tipo?${queryString}`);
  },
  clientesSaldo: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/clientes-saldo?${queryString}`);
  },
  movimientosPeriodo: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/movimientos-periodo?${queryString}`);
  },
  formasPago: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/formas-pago?${queryString}`);
  },
  evolucion: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/evolucion?${queryString}`);
  },
  evolucionSaldo: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/evolucion?${queryString}`);
  },
  saldosDashboard: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/saldos-dashboard?${queryString}`);
  },
  fuentesMetricas: async (origen) => {
    const q = origen ? `?origen=${encodeURIComponent(origen)}` : '';
    return apiRequest(`/informes/fuentes-metricas${q}`);
  },
  dashboardFicha: async (fichaId, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/informes/dashboard-ficha/${fichaId}${q ? `?${q}` : ''}`);
  },
  getMovimientosFondos: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/movimientos-fondos?${queryString}`);
  },
  getSaldosFondos: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/saldos-fondos?${queryString}`);
  },
  fondosPorTipoProveedor: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/fondos-por-tipo-proveedor?${queryString}`);
  },
  fondosPorTipoProveedorMovimientos: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/fondos-por-tipo-proveedor/movimientos?${queryString}`);
  },
  abonosCargadosCobrados: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/abonos-cargados-cobrados?${queryString}`);
  },
  cierreFondos: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/informes/cierre-fondos?${queryString}`);
  },
  proyeccionFondos: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v == null) return;
      if (k === 'incluir' && Array.isArray(v)) {
        v.forEach((item) => q.append('incluir', item));
      } else if (Array.isArray(v)) {
        q.set(k, v.length ? v.join(',') : '');
      } else if (v !== '') {
        q.set(k, v);
      } else {
        q.set(k, '');
      }
    });
    const queryString = q.toString();
    return apiRequest(`/informes/proyeccion-fondos${queryString ? `?${queryString}` : ''}`);
  },
  resultadosReales: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v == null) return;
      if (k === 'incluir' && Array.isArray(v)) {
        v.forEach((item) => q.append('incluir', item));
      } else if (Array.isArray(v)) {
        q.set(k, v.length ? v.join(',') : '');
      } else if (v !== '') {
        q.set(k, v);
      }
    });
    const queryString = q.toString();
    return apiRequest(`/informes/resultados-reales${queryString ? `?${queryString}` : ''}`);
  },
  resultadosRealesDetalle: async (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v == null) return;
      if (k === 'incluir' && Array.isArray(v)) {
        v.forEach((item) => q.append('incluir', item));
      } else if (Array.isArray(v)) {
        q.set(k, v.length ? v.join(',') : '');
      } else if (v !== '') {
        q.set(k, v);
      }
    });
    const queryString = q.toString();
    return apiRequest(`/informes/resultados-reales/detalle${queryString ? `?${queryString}` : ''}`);
  },
};

export const conciliacionesAPI = {
  listParsers: async () => apiRequest('/conciliaciones-bancarias/parsers'),
  listar: async (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return apiRequest(`/conciliaciones-bancarias${q ? `?${q}` : ''}`);
  },
  getById: async (id) => apiRequest(`/conciliaciones-bancarias/${id}`),
  crear: async (formData) => {
    const token = getToken();
    const response = await fetch(`${API_URL}/conciliaciones-bancarias`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const text = await response.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(text.slice(0, 200) || 'Error al subir extracto');
    }
    if (!response.ok) throw new Error(data.message || 'Error al crear conciliación');
    return data;
  },
  ejecutarMatch: async (id) =>
    apiRequest(`/conciliaciones-bancarias/${id}/ejecutar-match`, { method: 'POST' }),
  matchManual: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/matches`, { method: 'POST', body: JSON.stringify(body) }),
  acreditarCheques: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/acreditar-cheques`, {
      method: 'POST',
      body: JSON.stringify({
        linea_ids: body.linea_ids || body.linea_banco_ids || [],
        cheque_ids: body.cheque_ids || body.cheque_cartera_ids || [],
      }),
    }),
  rechazarCheques: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/rechazar-cheques`, {
      method: 'POST',
      body: JSON.stringify({
        linea_ids: body.linea_ids || body.linea_banco_ids || [],
        cheque_ids: body.cheque_ids || body.cheque_cartera_ids || [],
      }),
    }),
  importar: async (id, body) => {
    const payload = Array.isArray(body)
      ? { linea_ids: body }
      : {
          linea_ids: body.linea_ids || body.linea_banco_ids || [],
          destino: body.destino,
          tipo_proveedor_id: body.tipo_proveedor_id ?? null,
          cliente_id: body.cliente_id ?? null,
          proveedor_id: body.proveedor_id ?? null,
          empleado_id: body.empleado_id ?? null,
          cheque_cartera_ids: body.cheque_cartera_ids ?? null,
          caja_origen_id: body.caja_origen_id ?? null,
          ...(body.confirmar_saldo_negativo ? { confirmar_saldo_negativo: true } : {}),
        };
    return apiRequest(`/conciliaciones-bancarias/${id}/importar`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  matchGrupo: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/matches/grupo`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cancelarSistema: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/matches/cancelar-sistema`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  ignorar: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/matches/ignorar`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  revertir: async (id, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/matches/revertir`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  moverLineaCuenta: async (id, mfId, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/movimientos/${mfId}/mover-cuenta`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  moverMovimientoSistema: async (id, mfId, body) =>
    apiRequest(`/conciliaciones-bancarias/${id}/movimientos/${mfId}/mover-cuenta`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cerrar: async (id) =>
    apiRequest(`/conciliaciones-bancarias/${id}/cerrar`, { method: 'POST' }),
  eliminar: async (id) =>
    apiRequest(`/conciliaciones-bancarias/${id}`, { method: 'DELETE' }),
};

export const sistemaAPI = {
  getEstadoMigraciones: async () => apiRequest('/sistema/estado-migraciones'),
  reEjecutarMigraciones: async () =>
    apiRequest('/sistema/re-ejecutar-migraciones', { method: 'POST' }),
};

export const puestaEnMarchaAPI = {
  registrarChequeCartera: async (data) =>
    apiRequest('/puesta-en-marcha/cheque-cartera', { method: 'POST', body: JSON.stringify(data) }),
  updateSaldoCaja: async (id, saldo_inicial) =>
    apiRequest(`/puesta-en-marcha/saldo-caja/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ saldo_inicial }),
    }),
  updateSaldoCuenta: async (id, saldo_inicial) =>
    apiRequest(`/puesta-en-marcha/saldo-cuenta/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ saldo_inicial }),
    }),
};

export const facturacionAPI = {
  meta: () => apiRequest('/facturacion/meta'),
  candidatos: (params) => apiRequest(`/facturacion/candidatos?${new URLSearchParams(params)}`),
  listar: () => apiRequest('/facturacion'),
  emitir: (body) => apiRequest('/facturacion/emitir', { method: 'POST', body: JSON.stringify(body) }),
  liberar: (id) => apiRequest(`/facturacion/${id}/liberar`, { method: 'POST', body: JSON.stringify({}) }),
  diagnostico: () => apiRequest('/facturacion/diagnostico', { method: 'POST', body: JSON.stringify({}) }),
  abrirPdf: async (id) => {
    const token = getToken();
    const response = await fetch(`${API_URL}/facturacion/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'No se pudo abrir el PDF');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
  },
};

export const configuracionAPI = {
  getSistema: async () => apiRequest('/configuracion/sistema'),
  saveSistema: async (body) =>
    apiRequest('/configuracion/sistema', { method: 'PUT', body: JSON.stringify(body) }),
  saveZonaHoraria: async (zona_horaria) =>
    apiRequest('/configuracion/zona-horaria', { method: 'PUT', body: JSON.stringify({ zona_horaria }) }),
  getHorariosUso: async () => apiRequest('/configuracion/horarios-uso'),
  saveHorariosUso: async (body) =>
    apiRequest('/configuracion/horarios-uso', { method: 'PUT', body: JSON.stringify(body) }),
  uploadLogo: async (logo_base64, mime_type) =>
    apiRequest('/configuracion/logo', { method: 'PUT', body: JSON.stringify({ logo_base64, mime_type }) }),
  deleteLogo: async () => apiRequest('/configuracion/logo', { method: 'DELETE' }),
};

// Fichas custom del dashboard
export const dashboardFichasAPI = {
  listar: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/dashboard-fichas?${queryString}`);
  },
  obtener: async (id) => apiRequest(`/dashboard-fichas/${id}`),
  crear: async (body) => apiRequest('/dashboard-fichas', { method: 'POST', body: JSON.stringify(body) }),
  actualizar: async (id, body) => apiRequest(`/dashboard-fichas/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  eliminar: async (id) => apiRequest(`/dashboard-fichas/${id}`, { method: 'DELETE' }),
};

